/**
 * Statsig Event Types - Type Definitions Only
 *
 * This file contains only TypeScript type definitions, interfaces, and enums.
 * Constants and utilities are in separate files for better separation of concerns.
 */

import { NotificationPermissionState } from "@types";

// ===== ENUMS =====

export enum AuthSource {
  MEMBER_PROTECTOR = "member_protector",
  GAME_ENTRY = "game_entry",
  BOTTOM_NAVIGATION_CENTER_BUTTON = "bottom_navigation_center_button",
  BOTTOM_NAVIGATION_PROFILE_BUTTON = "bottom_navigation_profile_button",
  SHARE_BUTTON = "share_button",
  DAILY_REWARD_PAGE_CLAIM_BUTTON = "daily_reward_page_claim_button",
  VIP_BUTTON = "vip_button",
  SLOTHACK_BUTTON = "slothack_button",
  PLINKO_GAME_PLAY_BUTTON = "plinko_game_play_button",
  REFERRAL_PAGE_GET_STARTED_BUTTON = "referral_page_get_started_button",
  PAGE_HEADER_REGISTER_BUTTON = "page_header_register_button",
  CASHBACK_PAGE_CLAIM_BUTTON = "cashback_page_claim_button",
  MINI_PROMOTION_REGISTER_BANNER = "mini_promotion_register_banner",
  MINI_PROMOTION_DEPOSIT_BANNER = "mini_promotion_deposit_banner",
  MINI_PROMOTION_GAME_BANNER = "mini_promotion_game_banner",
  PLINKO_GAME_RECORDS_BUTTON = "plinko_game_records_button",
  PLINKO_GAME_GET_MORE_BALLS_BUTTON = "plinko_game_get_more_balls_button",
  PAGE_HEADER_LOGIN_BUTTON = "page_header_login_button",
}

export enum RegistrationMethod {
  USERNAME_PASSWORD = "username_password",
  PHONE_PIN = "phone_pin",
  GOOGLE_LOGIN = "google_login",
}

export enum OTPMethod {
  SMS = "sms",
  EMAIL = "email",
  VOICE = "voice",
}

export enum RegistrationStep {
  CLICK_REGISTER = "click_register",
  FORM_SUBMIT = "form_submit",
  OTP_SENT = "otp_sent",
  OTP_VERIFY = "otp_verify",
  REGISTRATION_COMPLETE = "registration_complete",
}

export enum DuplicateField {
  USERNAME = "username",
  PHONE = "phone",
  EMAIL = "email",
}

export enum ErrorSeverity {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

// ===== DEPOSIT FLOW ENUMS =====

export enum DepositSource {
  PROFILE_PAGE = "profile_page",
  BOTTOM_NAVIGATION = "bottom_navigation",
  PROMOTION_BANNER = "promotion_banner",
  MEMBER_PROTECTOR = "member_protector",
  DIRECT_LINK = "direct_link",
  AUTO_REDIRECT = "auto_redirect",
  BALANCE_BADGE = "balance_badge",
  TRANSACTION_PAGE = "transaction_page",
  PROMOTION_INELIGIBLE_CTA = "promotion_ineligible_cta",
}

export enum DepositMethod {
  BANK_TRANSFER = "bank_transfer",
  CREDIT_CARD = "credit_card",
  E_WALLET = "e_wallet",
  CRYPTO = "crypto",
  MOBILE_PAYMENT = "mobile_payment",
  AUTO_FAST = "auto_fast",
}

export enum DepositStep {
  CLICK_DEPOSIT = 1,
  SELECT_GROUP = 2,
  SELECT_METHOD = 3,
  SELECT_AMOUNT = 4,
  SUBMIT_TOP_UP = 5,
  SUBMIT_DEPOSIT = 6,
  DEPOSIT_COMPLETE = 7,
}

export enum NotificationInAppSource {
  PRIZES_PAGE = "prizes_page",
}

export enum NotificationAction {
  ALLOW = "allow",
  BLOCK = "block",
  CANCEL = "cancel",
  DISMISS = "dismiss",
}

export enum OTPSource {
  FORGOT_PASSWORD = "forgot_password",
  REGISTER = "register",
  LOGIN = "login",
}

export enum LanguageChangeSource {
  LANGUAGE_SELECT = "language_select",
  LANGUAGE_PAGE = "language_page",
}

export const IframeDialogCloseReason = {
  MANUAL: "manual",
  GO_BACK_HISTORY: "go_back_history",
} as const;

export const DepositIframeCloseReason = {
  ...IframeDialogCloseReason,
  DEPOSIT_SUCCESS: "deposit_success",
  DEPOSIT_FAILED: "deposit_failed",
} as const;

export const DepositNativeCloseReason = {
  ...IframeDialogCloseReason,
  DEPOSIT_SUCCESS: "deposit_success",
  DEPOSIT_FAILED: "deposit_failed",
  GO_BACK_TOP_UP: "go_back_top_up",
  TIMEOUT: "timeout",
} as const;

// ===== REGISTRATION FLOW INTERFACES =====

export interface RegistrationClickMetadata {
  source: AuthSource;
  buttonText?: string;
  previousPage?: string;
  campaignId?: string;
  referralCode?: string;
}

export interface RegistrationFormFocusMetadata {
  method?: RegistrationMethod;
  pathname: string;
  fromPathname?: string;
  search?: string;
}

export interface RegistrationFormSubmitMetadata {
  method: RegistrationMethod;
  step: RegistrationStep;
  formValidationErrors?: string[];
  timeSpentOnForm?: number; // seconds
  retryCount?: number;
  autocompleteUsed?: boolean;
}

export interface OTPSentMetadata {
  method: OTPMethod;
  step: RegistrationStep;
  phoneCountryCode?: string;
  retryCount?: number;
  deliveryTime?: number; // milliseconds
}

export interface OTPVerificationSuccessMetadata {
  method: OTPMethod;
  step: RegistrationStep;
  attemptsCount: number;
  timeToVerify?: number; // seconds from OTP sent
}

export interface OTPFailedAttemptMetadata {
  method: OTPMethod;
  step: RegistrationStep;
  attemptsCount: number;
  error: any;
  timeFromOTPSent?: number; // seconds
  source?: OTPSource;
}

export interface RegistrationCompleteMetadata {
  method: RegistrationMethod;
  step: RegistrationStep;
  hasReferralCode: boolean;
  referralCode?: string;
  totalTimeToComplete: number; // seconds from first click
  stepsCompleted: RegistrationStep[];
  phoneCountryCode?: string;
  campaignId?: string;
}

export interface RegistrationDuplicateErrorMetadata {
  method: RegistrationMethod;
  duplicateField: DuplicateField;
  errorMessage: string;
  step: RegistrationStep;
  previousAttempts?: number;
}

// ===== DEPOSIT FLOW INTERFACES =====

export interface DepositClickMetadata {
  source: DepositSource;
  previousPage?: string;
  buttonText?: string;
  accountBalance?: number;
}

export interface DepositMethodGroupMetadata {
  source: DepositSource;
  step: DepositStep;
  groupId: string;
  groupName: string;
  availableMethods?: string[];
  timeOnSelection?: number; // seconds
}

export interface DepositMethodIndividualMetadata {
  source: DepositSource;
  step: DepositStep;
  methodId: string;
  methodName: string;
  methodType: DepositMethod;
  groupId?: string;
  isRecommended?: boolean;
  fees?: number;
  processingTime?: string;
}

export interface DepositAmountMetadata {
  source: DepositSource;
  step: DepositStep;
  amount: number;
  isQuickAmount: boolean;
  selectedMethod: string;
  bonusOffered?: number;
  currency: string;
}

export interface DepositSubmitMetadata {
  source: DepositSource;
  step: DepositStep;
  amount: number;
  method: string;
  methodId: string;
  methodKey: string;
  depositGroupId?: string;
  depositGroupName?: string;
  currency: string;
  estimatedProcessingTime?: string;
}

export interface AutoFastTriggeredMetadata {
  source: DepositSource;
  step: DepositStep;
  originalMethod?: string;
  autoSelectedMethod: string;
  amount: number;
  reason: string; // e.g., "fastest_processing", "lowest_fees"
  timeSaved?: string;
}

export interface DepositSuccessMetadata {
  source: DepositSource;
  step: DepositStep;
  amount: number;
  method: DepositMethod;
  methodId: string;
  transactionId: string;
  currency: string;
  actualProcessingTime?: number; // seconds
  feesCharged?: number;
  bonusAwarded?: number;
  totalDepositTime?: number; // seconds from first click
}

export interface DepositFailedMetadata {
  source: DepositSource;
  step: DepositStep;
  amount: number;
  method: DepositMethod;
  methodId: string;
  currency: string;
  errorCode: string;
  errorMessage: string;
  errorType: string; // e.g., "insufficient_funds", "method_unavailable", "timeout"
  retryAttempt?: number;
  failurePoint?: string; // e.g., "authorization", "processing", "confirmation"
}

// Reusable options used when tracking deposit creation result (success/failure)
export interface DepositCreateResultOptions {
  source?: DepositSource;
  currency?: string;
  methodId?: string;
  methodName?: string;
  orderRef?: string;
  depositGroupId?: string;
  depositGroupName?: string;
  methodKey?: string;
  amount?: number;
  providerKey?: string;
  paymentMethod?: string;
  payDataType?: string;
}

export interface CloseDepositIframeMetadata {
  reason: (typeof DepositIframeCloseReason)[keyof typeof DepositIframeCloseReason];
  orderRef: string;
}

export interface CloseDepositNativeMetadata {
  reason: (typeof DepositNativeCloseReason)[keyof typeof DepositNativeCloseReason];
  orderRef?: string;
}

// ===== NOTIFICATIONS INTERFACES =====

export interface NotificationInAppPromptShownMetadata {
  source: NotificationInAppSource;
}

export interface NotificationInAppPromptResponseMetadata {
  action: NotificationAction;
}

export interface NotificationBrowserPromptResponseMetadata {
  action: NotificationAction;
}

export interface NotificationSubscriptionStatusMetadata {
  status: NotificationPermissionState;
}

// ===== GENERIC INTERFACES =====

export interface EventMetadata {
  [key: string]: any;
}

// ===== TYPE UNIONS =====

export type RegistrationEventMetadata =
  | RegistrationClickMetadata
  | RegistrationFormSubmitMetadata
  | OTPSentMetadata
  | OTPVerificationSuccessMetadata
  | OTPFailedAttemptMetadata
  | RegistrationCompleteMetadata
  | RegistrationDuplicateErrorMetadata;

export type DepositEventMetadata =
  | DepositClickMetadata
  | DepositMethodGroupMetadata
  | DepositMethodIndividualMetadata
  | DepositAmountMetadata
  | DepositSubmitMetadata
  | AutoFastTriggeredMetadata
  | DepositSuccessMetadata
  | DepositFailedMetadata;

export type NotificationEventMetadata =
  | NotificationInAppPromptShownMetadata
  | NotificationInAppPromptResponseMetadata
  | NotificationBrowserPromptResponseMetadata
  | NotificationSubscriptionStatusMetadata;

export type AllEventMetadata =
  | RegistrationEventMetadata
  | DepositEventMetadata
  | NotificationEventMetadata
  | EventMetadata;

// ===== DEPOSIT EMPTY STATES =====

export interface DepositGroupsEmptyMetadata {
  groupsOriginalCount?: number;
  groupsWithActiveMethodsCount?: number;
  error?: string;
  path?: string;
  query?: string;
}

export interface DepositMethodsEmptyMetadata {
  methodsOriginalCount?: number;
  methodsActiveCount?: number;
  simulateDeposit?: boolean;
  ignoreMintMethod?: boolean;
  mintAdded?: boolean;
  error?: string;
  path?: string;
  query?: string;
}
